/**
 * Zod Schemas for Answer Payload Validation
 *
 * Provides runtime type validation for all AnswerPayload types.
 * Ensures type safety beyond TypeScript's compile-time checks.
 *
 * Moved from src/utils/answerPayload.schema.ts in R31 (DDD Fase 4).
 */

import { z } from 'zod';

// ============================================================
// INDIVIDUAL PAYLOAD SCHEMAS
// ============================================================

/** Choice selection (multiple choice, bar chart, line chart) */
export const ChoicePayloadSchema = z.object({
  kind: z.literal('choice'),
  selectedIndex: z.number().int().nonnegative()
});

/** Text input (fill-in, column arithmetic) */
export const TextPayloadSchema = z.object({
  kind: z.literal('text'),
  value: z.string()
});

/** Time input (clock questions) */
export const TimePayloadSchema = z.object({
  kind: z.literal('time'),
  value: z.object({
    hours: z.number().int().min(0).max(23),
    minutes: z.number().int().min(0).max(59),
    format: z.literal('24h')
  })
});

/** Token set (money questions) */
export const TokenSetPayloadSchema = z.object({
  kind: z.literal('token_set'),
  value: z.object({
    tokens: z.array(z.object({
      valueCents: z.number().int().positive(),
      count: z.number().int().nonnegative()
    })),
    totalCents: z.number().int().nonnegative()
  })
});

/** Mapping pairs (matching questions) */
export const MappingPayloadSchema = z.object({
  kind: z.literal('mapping'),
  pairs: z.array(z.tuple([z.number().int(), z.number().int()]))
});

/** Ordered sequence (ordering questions) */
export const OrderingPayloadSchema = z.object({
  kind: z.literal('ordering'),
  sequence: z.array(z.number().int())
});

/** Cell selection (schedule reading, table reading) */
export const CellSelectionPayloadSchema = z.object({
  kind: z.literal('cell_selection'),
  row: z.number().int().nonnegative(),
  col: z.number().int().nonnegative()
});

/** Key-value map (ratio table, drag-drop-fill) */
export const KeyValuePayloadSchema = z.object({
  kind: z.literal('key_value'),
  values: z.record(z.string(), z.string())
});

/** Segment selection (text highlight) */
export const SegmentPayloadSchema = z.object({
  kind: z.literal('segment'),
  segmentId: z.string()
});

/** Sequence of values (number sequence) */
export const ValueSequencePayloadSchema = z.object({
  kind: z.literal('value_sequence'),
  values: z.array(z.string())
});

/** Visual data point selection (charts/schedules) */
export const VisualDataPayloadSchema = z.object({
  kind: z.literal('visual_data'),
  referenceId: z.string(),
  rawIndex: z.number().int().optional()
});

/** Two-step choice (idiom_literal_judge two_step_judge MC mode) */
export const TwoStepChoicePayloadSchema = z.object({
  kind: z.literal('two_step_choice'),
  step1: z.number().int().nonnegative(),
  step2: z.number().int().nonnegative().optional()
});

// ============================================================
// UNION SCHEMA
// ============================================================

/**
 * Discriminated union of all answer payload types
 * Matches the AnswerPayload type from answerProtocol.ts
 */
export const AnswerPayloadSchema = z.discriminatedUnion('kind', [ChoicePayloadSchema, TextPayloadSchema, TimePayloadSchema, TokenSetPayloadSchema, MappingPayloadSchema, OrderingPayloadSchema, CellSelectionPayloadSchema, KeyValuePayloadSchema, SegmentPayloadSchema, ValueSequencePayloadSchema, VisualDataPayloadSchema, TwoStepChoicePayloadSchema]);

// ============================================================
// VALIDATION RESULT SCHEMA
// ============================================================

/**
 * Schema for ValidationResult
 * Ensures validators return properly structured results
 */
export const ValidationResultSchema = z.object({
  isCorrect: z.boolean(),
  distractorIndex: z.number().int().nonnegative().optional(),
  displayAnswer: z.string().optional(),
  correctAnswerDisplay: z.string().optional(),
  explanationKey: z.string().optional(),
  debugTrace: z.string().optional(),
  errorType: z.string().optional(),
  delta: z.number().optional()
});

// ============================================================
// TYPE INFERENCE
// ============================================================

/** Infer TypeScript types from Zod schemas */
export type ChoicePayload = z.infer<typeof ChoicePayloadSchema>;
export type TextPayload = z.infer<typeof TextPayloadSchema>;
export type TimePayload = z.infer<typeof TimePayloadSchema>;
export type TokenSetPayload = z.infer<typeof TokenSetPayloadSchema>;
export type MappingPayload = z.infer<typeof MappingPayloadSchema>;
export type OrderingPayload = z.infer<typeof OrderingPayloadSchema>;
export type CellSelectionPayload = z.infer<typeof CellSelectionPayloadSchema>;
export type KeyValuePayload = z.infer<typeof KeyValuePayloadSchema>;
export type SegmentPayload = z.infer<typeof SegmentPayloadSchema>;
export type ValueSequencePayload = z.infer<typeof ValueSequencePayloadSchema>;
export type VisualDataPayload = z.infer<typeof VisualDataPayloadSchema>;
export type TwoStepChoicePayload = z.infer<typeof TwoStepChoicePayloadSchema>;
export type AnswerPayload = z.infer<typeof AnswerPayloadSchema>;
export type ValidationResult = z.infer<typeof ValidationResultSchema>;

// ============================================================
// VALIDATION HELPERS
// ============================================================

/**
 * Validate an answer payload against its schema
 */
export function validateAnswerPayload(payload: unknown, expectedKind?: string): {
  success: boolean;
  data?: AnswerPayload;
  error?: z.ZodError;
} {
  const result = AnswerPayloadSchema.safeParse(payload);
  if (!result.success) {
    return {
      success: false,
      error: result.error
    };
  }

  // Optional: Check if kind matches expected
  if (expectedKind && result.data.kind !== expectedKind) {
    return {
      success: false,
      error: new z.ZodError([{
        code: 'custom',
        path: ['kind'],
        message: `Expected kind '${expectedKind}', got '${result.data.kind}'`
      }])
    };
  }
  return {
    success: true,
    data: result.data
  };
}

/**
 * Validate a validation result
 */
export function validateValidationResult(result: unknown): {
  success: boolean;
  data?: ValidationResult;
  error?: z.ZodError;
} {
  const validation = ValidationResultSchema.safeParse(result);
  if (!validation.success) {
    return {
      success: false,
      error: validation.error
    };
  }
  return {
    success: true,
    data: validation.data
  };
}

/**
 * Type guard: Check if payload is a specific kind
 */
export function isPayloadKind<K extends AnswerPayload['kind']>(payload: AnswerPayload, kind: K): payload is Extract<AnswerPayload, {
  kind: K;
}> {
  return payload.kind === kind;
}

// ============================================================
// SCHEMA MAP FOR DYNAMIC VALIDATION
// ============================================================

/**
 * Map of payload kinds to their schemas
 * Useful for dynamic validation based on question type
 */
export const PayloadSchemaMap = {
  choice: ChoicePayloadSchema,
  text: TextPayloadSchema,
  time: TimePayloadSchema,
  token_set: TokenSetPayloadSchema,
  mapping: MappingPayloadSchema,
  ordering: OrderingPayloadSchema,
  cell_selection: CellSelectionPayloadSchema,
  key_value: KeyValuePayloadSchema,
  segment: SegmentPayloadSchema,
  value_sequence: ValueSequencePayloadSchema,
  visual_data: VisualDataPayloadSchema,
  two_step_choice: TwoStepChoicePayloadSchema
} as const;

/**
 * Validate payload against a specific kind's schema
 */
export function validatePayloadByKind(payload: unknown, kind: keyof typeof PayloadSchemaMap): {
  success: boolean;
  data?: AnswerPayload;
  error?: z.ZodError;
} {
  const schema = PayloadSchemaMap[kind];
  const result = schema.safeParse(payload);
  if (!result.success) {
    return {
      success: false,
      error: result.error
    };
  }
  return {
    success: true,
    data: result.data as AnswerPayload
  };
}